/**
 * Supabase client for frontend
 * Used for Realtime subscriptions and Storage operations
 */

import { createClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { logClientCreate, logClientEvict, logClientUpdate, logClientClear, tokenTail } from './realtime-debug';

const supabaseUrl = `https://${projectId}.supabase.co`;

// Shared anon client — один инстанс на всё приложение.
// Session management отключён: приложение управляет токенами вручную через localStorage['access_token'].
export const supabase = createClient(supabaseUrl, publicAnonKey, {
  auth: {
    flowType: 'implicit',
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
});

// Each client needs a unique storageKey — shared keys trigger "Multiple GoTrueClient" warning.
let _clientSeq = 0;

// Max 2 slots: current token + one in-flight during rotation.
// Keyed by access token string.
const authedClientCache = new Map<string, ReturnType<typeof createClient>>();

function evictOldest() {
  const [[key, client]] = authedClientCache.entries();
  logClientEvict(tokenTail(key));
  try { client.removeAllChannels(); } catch (e) {
    console.error('[RT:CLIENT] removeAllChannels() threw during eviction:', e);
  }
  authedClientCache.delete(key);
}

/**
 * Returns a stable authenticated Supabase client for the given token.
 * At most 2 clients live simultaneously (current + one during token rotation).
 * Falls back to the shared anon client when token is missing.
 */
export function getSupabaseClient(accessToken?: string | null) {
  if (!accessToken) return supabase;

  const cached = authedClientCache.get(accessToken);
  if (cached) return cached;

  if (authedClientCache.size >= 2) evictOldest();

  const client = createClient(supabaseUrl, publicAnonKey, {
    global: { headers: { Authorization: `Bearer ${accessToken}` } },
    auth: {
      // Unique per instance — prevents "Multiple GoTrueClient instances" warning
      // that fires when two clients share the same storageKey.
      storageKey: `moms-auth-${_clientSeq++}`,
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
  });

  client.realtime.setAuth(accessToken);
  authedClientCache.set(accessToken, client);
  logClientCreate(_clientSeq - 1, tokenTail(accessToken));
  return client;
}

/**
 * Call after a successful token refresh to update realtime auth on all
 * cached clients without creating a new GoTrueClient instance.
 */
export function updateSupabaseAuth(newToken: string) {
  logClientUpdate(tokenTail(newToken));
  for (const client of authedClientCache.values()) {
    try { client.realtime.setAuth(newToken); } catch (e) {
      console.error('[RT:CLIENT] setAuth() threw during updateSupabaseAuth:', e);
    }
  }
}

/**
 * Tears down all cached clients and their realtime connections.
 * Call on logout.
 */
export function clearSupabaseClientCache() {
  logClientClear(authedClientCache.size);
  for (const client of authedClientCache.values()) {
    try { client.removeAllChannels(); } catch (e) {
      console.error('[RT:CLIENT] removeAllChannels() threw during cache clear:', e);
    }
  }
  authedClientCache.clear();
}
